import { useState } from "react";
import { ChevronDown, ChevronUp, Settings2 } from "lucide-react";

export const COURTS = [
  "臺灣宜蘭地方法院", "臺灣苗栗地方法院", "臺灣臺中地方法院", "臺灣南投地方法院",
  "臺灣新竹地方法院", "臺灣彰化地方法院", "臺灣雲林地方法院", "臺灣嘉義地方法院",
  "臺灣臺南地方法院", "臺灣高雄地方法院", "臺灣屏東地方法院", "臺灣花蓮地方法院",
  "臺灣臺東地方法院", "臺灣士林地方法院", "臺灣新北地方法院", "臺灣桃園地方法院",
];

export const DEFAULT_FORM = {
  ownerName: "", ownerId: "", company: "", license: "", phone: "", email: "",
  address: "", propertyName: "", propertyAddress: "",
  consultant: "", consultantEmail: "", signDate: "", court: "臺灣宜蘭地方法院", contractYears: 1,
  guaranteedRevenue: "", campUnits: "", minRevenue: "", earlyBirdMonth: "",
  // 未達業績解約門檻條款（預設關閉，未勾選啟用時不出現在合約上）
  minRevEnabled: false,
  specialTerms: "",
  bankName: "", bankCode: "", branchName: "", branchCode: "", accountName: "", accountNumber: "",
  // 階梯式月費（月付方案專用，預設關閉，不會顯示在合約上）
  revTierEnabled: false,
  revTierThreshold: "",   // 例：430000
  revTierBelowFee: "",    // 例：23000
  revTierBelowCommission: "", // 例：0
};

const Field = ({ label, children }) => (
  <label className="block">
    <span className="text-xs font-medium text-slate-500 mb-1 block">{label}</span>
    {children}
  </label>
);

const inputCls = "w-full text-sm border border-slate-200 rounded-lg px-3 py-2 bg-white text-[#0A2540] focus:outline-none focus:ring-2 focus:ring-[#FF6B00]/30 focus:border-[#FF6B00] placeholder:text-slate-300";

const Section = ({ title, children }) => (
  <div className="mb-5">
    <div className="text-sm font-bold text-[#0A2540] mb-3 flex items-center gap-2">
      <span className="w-1.5 h-1.5 rounded-full bg-[#FF6B00]" />{title}
    </div>
    {children}
  </div>
);

export const ContractFormToggle = ({ open, onToggle }) => (
  <button
    onClick={onToggle}
    data-testid="contract-form-toggle"
    className={`flex items-center gap-1.5 text-xs px-3 py-2 rounded-lg border transition-colors font-medium ${
      open
        ? "bg-[#0A2540] text-white border-[#0A2540]"
        : "bg-white text-slate-500 border-slate-200 hover:border-slate-300 hover:text-slate-700"
    }`}
  >
    <Settings2 className="w-3.5 h-3.5" />
    <span className="hidden sm:inline">填寫合約資料</span>
    <span className="sm:hidden">填寫</span>
    {open ? <ChevronUp className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />}
  </button>
);

export const ContractForm = ({ form, setField, selectedPlan, units, setUnits }) => {
  const bind = (key, extra = {}) => ({
    value: form[key],
    onChange: (e) => setField(key, e.target.value),
    className: inputCls,
    "data-testid": `form-${key}`,
    ...extra,
  });

  return (
    <div data-testid="contract-form-panel" className="bg-white border border-slate-200 rounded-2xl shadow-sm p-5 sm:p-6 mb-6">
      <div className="text-xs text-slate-400 mb-5 leading-relaxed">
        此區塊供客戶經理填寫，所有欄位皆為選填。填入的內容會即時帶入下方合約預覽；未填寫的欄位會以空白線呈現，可於列印後手寫。
      </div>

      <Section title="方案參數">
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
          <Field label="合約年限">
            <select {...bind("contractYears")} onChange={(e) => setField("contractYears", parseInt(e.target.value, 10))}>
              <option value={1}>1 年</option>
              <option value={2}>2 年</option>
              <option value={3}>3 年</option>
            </select>
          </Field>
          {["plan1", "plan5", "plan4"].includes(selectedPlan) && (
            <Field label="物件棟數">
              <input type="number" min="1" max="20" value={units} data-testid="form-units"
                onChange={(e) => setUnits(Math.max(1, Math.min(20, parseInt(e.target.value, 10) || 1)))}
                className={inputCls} />
            </Field>
          )}
          {["plan1", "plan5", "plan4"].includes(selectedPlan) && (
            <Field label="早鳥優惠適用月份">
              <input {...bind("earlyBirdMonth", { placeholder: "例：2026 年 8 月" })} />
            </Field>
          )}
          {selectedPlan === "plan2" && (
            <Field label="保障營業額（NT$/月）">
              <input type="number" {...bind("guaranteedRevenue", { placeholder: "例：150000" })} />
            </Field>
          )}
          {selectedPlan === "plan2" && (
            <Field label="營運範圍（帳/房數說明）">
              <input {...bind("campUnits", { placeholder: "例：共計 10 帳（含露營車及合掌屋）" })} />
            </Field>
          )}
          <Field label="管轄法院（依物件所在地）">
            <select {...bind("court")}>
              {COURTS.map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
          </Field>
          <Field label="簽約顧問">
            <input {...bind("consultant", { placeholder: "顧問姓名" })} />
          </Field>
          <Field label="簽約顧問電子郵件">
            <input {...bind("consultantEmail", { placeholder: "例：service@lakeze.com" })} />
          </Field>
          <Field label="簽約日期">
            <input type="date" {...bind("signDate")} />
          </Field>
        </div>
      </Section>

      <Section title="甲方基本資料">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
          <Field label="負責人名稱"><input {...bind("ownerName")} /></Field>
          <Field label="身分證字號 / 統一編號"><input {...bind("ownerId")} /></Field>
          <Field label="公司"><input {...bind("company")} /></Field>
          <Field label="民宿執照登記（若無免填）"><input {...bind("license")} /></Field>
          <Field label="聯絡電話"><input {...bind("phone")} /></Field>
          <Field label="電子郵件"><input {...bind("email")} /></Field>
          <Field label="戶籍地址"><input {...bind("address")} /></Field>
          <Field label="物件名稱"><input {...bind("propertyName")} /></Field>
          <Field label="物件地址"><input {...bind("propertyAddress")} /></Field>
        </div>
      </Section>

      <Section title="甲方銀行帳戶">
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
          <Field label="銀行名稱"><input {...bind("bankName")} /></Field>
          <Field label="金融代號"><input {...bind("bankCode")} /></Field>
          <Field label="分行名稱"><input {...bind("branchName")} /></Field>
          <Field label="分行代碼"><input {...bind("branchCode")} /></Field>
          <Field label="戶名"><input {...bind("accountName")} /></Field>
          <Field label="帳號"><input {...bind("accountNumber")} /></Field>
        </div>
      </Section>

      {selectedPlan !== "plan3" && (
        <MinRevSection form={form} setField={setField} />
      )}

      {["plan1", "plan5", "plan4"].includes(selectedPlan) && (
        <RevTierSection form={form} setField={setField} selectedPlan={selectedPlan} />
      )}

      <Section title="特別條款（選填）">
        <textarea
          {...bind("specialTerms", {
            placeholder: "如有個案特別約定（例如階梯式月費、特殊分潤條件），請在此輸入；未填寫則合約不顯示此區塊。每行一條。",
            rows: 4,
          })}
        />
      </Section>
    </div>
  );
};

// 見證場特別條款：營業額月費調整（月付方案專用）
// 預設收合、預設不啟用；只有客戶經理主動勾選『啟用』後才會出現在正式合約中，且僅適用於當前個案
const RevTierSection = ({ form, setField, selectedPlan }) => {
  const std = selectedPlan === "plan4" ? { fee: "38,000", rate: 25 } : selectedPlan === "plan5" ? { fee: "56,000", rate: 20 } : { fee: "78,000", rate: 20 };
  const [open, setOpen] = useState(false);
  const enabled = !!form.revTierEnabled;

  return (
    <div className="mb-5 border border-dashed border-slate-300 rounded-xl bg-slate-50/60">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        data-testid="rev-tier-toggle"
        className="w-full flex items-center justify-between px-4 py-3 text-left"
      >
        <div className="flex items-center gap-2 flex-wrap">
          <span className="w-1.5 h-1.5 rounded-full bg-[#FF6B00]" />
          <span className="text-sm font-bold text-[#0A2540]">見證場特別條款：營業額月費調整</span>
          {enabled && (
            <span className="text-[10px] font-bold text-[#FF6B00] bg-[#FF6B00]/10 border border-[#FF6B00]/30 px-2 py-0.5 rounded-full">
              合約中顯示
            </span>
          )}
          {!enabled && (
            <span className="text-[10px] font-medium text-slate-400 bg-white border border-slate-200 px-2 py-0.5 rounded-full">
              預設不顯示
            </span>
          )}
        </div>
        {open ? <ChevronUp className="w-4 h-4 text-slate-400" /> : <ChevronDown className="w-4 h-4 text-slate-400" />}
      </button>

      {open && (
        <div className="px-4 pb-4 border-t border-slate-200">
          <div className="text-xs text-slate-500 leading-relaxed mt-3 mb-3">
            此為「<strong>見證場</strong>」個案專屬條款：當甲方當月營業額 <strong>低於門檻</strong> 時，改採較低月費／抽成。
            <br /><span className="text-[#B8860B] font-semibold">僅適用於此合約，不適用其他標準合約</span>。未勾選『啟用』時合約上不會出現此區塊。
          </div>

          <label className="flex items-center gap-2 mb-4 cursor-pointer select-none">
            <input
              type="checkbox"
              checked={enabled}
              onChange={(e) => setField("revTierEnabled", e.target.checked)}
              data-testid="rev-tier-enabled"
              className="w-4 h-4 accent-[#FF6B00]"
            />
            <span className="text-sm font-semibold text-[#0A2540]">啟用見證場特別條款（將寫入本份合約）</span>
          </label>

          <div className={`grid grid-cols-1 sm:grid-cols-3 gap-3 ${enabled ? "" : "opacity-50 pointer-events-none"}`}>
            <Field label="當月營業額門檻（NT$）">
              <input
                type="number"
                value={form.revTierThreshold}
                onChange={(e) => setField("revTierThreshold", e.target.value)}
                placeholder="例：430000"
                data-testid="form-revTierThreshold"
                className={inputCls}
                disabled={!enabled}
              />
            </Field>
            <Field label="未達門檻・月費（NT$）">
              <input
                type="number"
                value={form.revTierBelowFee}
                onChange={(e) => setField("revTierBelowFee", e.target.value)}
                placeholder="例：23000"
                data-testid="form-revTierBelowFee"
                className={inputCls}
                disabled={!enabled}
              />
            </Field>
            <Field label="未達門檻・訂單抽成 (%)">
              <input
                type="number"
                min="0"
                max="100"
                step="0.5"
                value={form.revTierBelowCommission}
                onChange={(e) => setField("revTierBelowCommission", e.target.value)}
                placeholder="例：0"
                data-testid="form-revTierBelowCommission"
                className={inputCls}
                disabled={!enabled}
              />
            </Field>
          </div>

          <div className="mt-3 text-[11px] text-slate-400 leading-relaxed">
            達門檻以上維持標準月費 NT$ {std.fee} / 月 與 {std.rate}% 訂單抽成；未達門檻按上方設定計算。
          </div>
        </div>
      )}
    </div>
  );
};


// 未達業績解約門檻條款（月付方案 & 保障分潤方案適用）
// 預設收合、預設不啟用；只有客戶經理主動勾選『啟用』後才會出現在合約中
const MinRevSection = ({ form, setField }) => {
  const [open, setOpen] = useState(false);
  const enabled = !!form.minRevEnabled;

  return (
    <div className="mb-5 border border-dashed border-slate-300 rounded-xl bg-slate-50/60">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        data-testid="min-rev-toggle"
        className="w-full flex items-center justify-between px-4 py-3 text-left"
      >
        <div className="flex items-center gap-2 flex-wrap">
          <span className="w-1.5 h-1.5 rounded-full bg-[#FF6B00]" />
          <span className="text-sm font-bold text-[#0A2540]">未達業績解約門檻條款</span>
          {enabled && (
            <span className="text-[10px] font-bold text-[#FF6B00] bg-[#FF6B00]/10 border border-[#FF6B00]/30 px-2 py-0.5 rounded-full">
              合約中顯示
            </span>
          )}
          {!enabled && (
            <span className="text-[10px] font-medium text-slate-400 bg-white border border-slate-200 px-2 py-0.5 rounded-full">
              預設不顯示
            </span>
          )}
        </div>
        {open ? <ChevronUp className="w-4 h-4 text-slate-400" /> : <ChevronDown className="w-4 h-4 text-slate-400" />}
      </button>

      {open && (
        <div className="px-4 pb-4 border-t border-slate-200">
          <div className="text-xs text-slate-500 leading-relaxed mt-3 mb-3">
            此條款約定：自正式收費起算，如連續四個月皆未能達成設定之月營業額門檻，甲乙雙方均得書面通知解約，且不受違約條款規範。
            <br /><span className="text-[#B8860B] font-semibold">未勾選『啟用』時合約上不會出現此條款</span>。
          </div>

          <label className="flex items-center gap-2 mb-4 cursor-pointer select-none">
            <input
              type="checkbox"
              checked={enabled}
              onChange={(e) => setField("minRevEnabled", e.target.checked)}
              data-testid="min-rev-enabled"
              className="w-4 h-4 accent-[#FF6B00]"
            />
            <span className="text-sm font-semibold text-[#0A2540]">啟用未達業績解約門檻條款（將寫入本份合約）</span>
          </label>

          <div className={`grid grid-cols-1 sm:grid-cols-2 gap-3 ${enabled ? "" : "opacity-50 pointer-events-none"}`}>
            <Field label="解約門檻・月營業額（NT$）">
              <input
                type="number"
                value={form.minRevenue}
                onChange={(e) => setField("minRevenue", e.target.value)}
                placeholder="例：600000"
                data-testid="form-minRevenue"
                className={inputCls}
                disabled={!enabled}
              />
            </Field>
          </div>
        </div>
      )}
    </div>
  );
};
